import AddIcon from '@mui/icons-material/Add';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
// import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { Navigation } from '@toolpad/core';
import { Account, AppProvider, DashboardLayout as ToolpadDashboardLayout } from '@toolpad/core';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAccount } from '../../contexts/AccountContext';
import { useAuth } from '../../contexts/AuthContext';
// import { useBilling } from '../../contexts/BillingContext';
import { useNotifications } from '../../contexts/NotificationContext';
// import { usePricing } from '../../contexts/PricingContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useRecentProcurements } from '../../hooks/useRecentProcurements';
import { useRecentTenders } from '../../hooks/useRecentTenders';
import { BRANDING, getNavigationForUser } from '../../navigation';
import { PERMISSIONS } from '../../shared/constants/permissions';
import { ACTIVITY_TYPES, logActivity } from '../../utils/activityLogger';
import ThemeToggle from '../common/ThemeToggle';
import NotificationBell from '../Notifications/NotificationBell';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const ToolbarActions = () => {
  const { isLoggingOut } = useAuth();
  const { account, hasPermission } = useAccount();
  // const { ledger } = useBilling();
  // const { pricingSettings } = usePricing();
  const theme = useTheme();
  const navigate = useNavigate();

  // const hideBillingChip =
  //   (account?.clientSettings?.hideBillingFromUsers ?? true) && !hasPermission('billing.manage');

  const { mode, setMode } = useWorkspace();
  const canAccessProcurement =
    account?.clientSettings?.enableProcurementModule === true ||
    hasPermission(PERMISSIONS.SYSTEM_MANAGE);
  const canAccessBidding = account?.clientSettings?.enableBiddingModule !== false;

  const showWorkspaceToggle = canAccessProcurement && canAccessBidding;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {showWorkspaceToggle && (
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(_, newMode) => {
            if (newMode) {
              setMode(newMode);
            }
          }}
          size="small"
          sx={{
            mr: 2,
            display: 'flex',
            '& .MuiToggleButton-root': {
              color: 'rgba(255, 255, 255, 0.7)',
              borderColor: 'rgba(255, 255, 255, 0.23)',
              '&.Mui-selected': {
                color: '#ffffff',
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
              },
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
            },
          }}
        >
          <ToggleButton value="BID" sx={{ px: 2, fontWeight: 'bold' }}>
            Bidding
          </ToggleButton>
          <ToggleButton value="PROCURE" sx={{ px: 2, fontWeight: 'bold' }}>
            Procurement
          </ToggleButton>
        </ToggleButtonGroup>
      )}
      {/* 
      {ledger &&
        !hideBillingChip &&
        pricingSettings &&
        (account?.subscription?.status === 'trialing' ? (
          <Chip
            label="FREE TRIAL"
            color="success"
            variant="filled"
            onClick={() => navigate('/billing')}
            sx={{
              mr: 1,
              display: { xs: 'none', sm: 'flex' },
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '0.875rem',
            }}
          />
        ) : (
          <Chip
            label={
              ledger.balance > 0
                ? `$${(ledger.balance * pricingSettings.cost_per_credit_in_base).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : ledger.accumulated_usage > 0
                  ? `$${(ledger.accumulated_usage * pricingSettings.cost_per_credit_in_base).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Unbilled`
                  : `$0.00`
            }
            variant="filled"
            onClick={() => navigate('/billing')}
            sx={{
              mr: 1,
              display: { xs: 'none', sm: 'flex' },
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '0.875rem',
              backgroundColor: theme.palette.greyContrast,
              color: theme.palette.primary.contrastText,
            }}
          />
        ))}
      */}
      {mode === 'PROCURE' ? (
        hasPermission(PERMISSIONS.TENDER_CREATE) ? (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/procure/dashboard', { state: { openCreate: true } })}
            sx={{
              backgroundColor: theme.palette.bidlogiq?.brick || 'primary.main',
              color: 'white',
              fontWeight: 'bold',
              mr: 1,
              display: { xs: 'none', sm: 'flex' },
            }}
          >
            New Procurement
          </Button>
        ) : null
      ) : (
        <>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/tenders/new')}
            sx={{
              backgroundColor: theme.palette.bidlogiq?.brick || 'primary.main',
              color: 'white',
              fontWeight: 'bold',
              mr: 1,
              display: { xs: 'none', sm: 'flex' }, // Hide on very small screens if needed, or keep always visible
            }}
          >
            New Tender
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<AddIcon sx={{ color: 'success.contrastText' }} />}
            onClick={() => navigate('/tenders/new', { state: { isGrant: true } })}
            sx={{
              fontWeight: 'bold',
              mr: 1,
              display: { xs: 'none', sm: 'flex' },
            }}
          >
            New Grant
          </Button>
        </>
      )}
      <ThemeToggle />
      <NotificationBell />
      {isLoggingOut ? <CircularProgress size={24} color="inherit" /> : <Account />}
    </Box>
  );
};

function useAppRouter() {
  const navigate = useNavigate();
  const location = useLocation();
  return React.useMemo(
    () => ({
      pathname: location.pathname,
      searchParams: new URLSearchParams(location.search),
      navigate: (path: string | URL) => {
        if (typeof path === 'string') {
          // If it's a full URL string, extract pathname + search
          if (path.startsWith('http://') || path.startsWith('https://')) {
            const url = new URL(path);
            navigate(url.pathname + url.search);
          } else {
            // Intercept dashboard nested tender paths
            let finalPath = path;
            if (path.startsWith('/dashboard/tender/')) {
              finalPath = path.replace('/dashboard/tender/', '/tender/');
            }
            // Ensure path is absolute to prevent relative navigation bugs from nested pages
            const absolutePath = finalPath.startsWith('/') ? finalPath : `/${finalPath}`;
            navigate(absolutePath);
          }
        } else if (path instanceof URL) {
          navigate(path.pathname + path.search);
        } else {
          navigate(String(path));
        }
      },
    }),
    [location, navigate]
  );
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const muiTheme = useTheme();
  useMediaQuery(muiTheme.breakpoints.down('sm'));
  const router = useAppRouter();
  const { logout, isLoggingOut } = useAuth();
  const { account } = useAccount();
  const { showSnackbar } = useSnackbar();
  const { notifications } = useNotifications();
  const recentTenders = useRecentTenders(account);
  const recentProcurements = useRecentProcurements(account);

  // Show loading feedback when logout starts
  React.useEffect(() => {
    if (isLoggingOut) {
      showSnackbar('Signing out...', 'info', 3000, {
        backgroundColor: muiTheme.palette.bidlogiq.brick,
        color: muiTheme.palette.bidlogiq.white,
        fontSize: '1.2rem',
        fontWeight: 'bold',
        '& .MuiAlert-message': {
          fontSize: '1.2rem',
          fontWeight: 'bold',
        },
      });
    }
  }, [
    isLoggingOut,
    showSnackbar,
    muiTheme.palette.bidlogiq.brick,
    muiTheme.palette.bidlogiq.white,
  ]);

  // Auto-scroll sidebar when navigating to admin or when expanding the Admin Panel menu
  React.useEffect(() => {
    // 1. Auto-scroll on navigation
    if (router.pathname.startsWith('/admin')) {
      setTimeout(() => {
        const activeMenuItem = document.querySelector('.MuiDrawer-paper .Mui-selected');
        if (activeMenuItem) {
          activeMenuItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          const drawer = document.querySelector('.MuiDrawer-paper');
          if (drawer) {
            drawer.scrollTo({ top: drawer.scrollHeight, behavior: 'smooth' });
          }
        }
      }, 100);
    }

    // 2. Add click listener to the Admin Panel item to scroll when expanded
    const handleSidebarClick = (e: Event) => {
      const target = e.target as HTMLElement;
      // Look for the closest list item button
      const listItem = target.closest('.MuiListItemButton-root');
      if (listItem?.textContent?.includes('Admin Panel')) {
        setTimeout(() => {
          // Scroll the Admin Panel list item to the top of the view so its expanded options are visible
          listItem.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 300); // Wait for the accordion expand animation
      }
    };

    const drawer = document.querySelector('.MuiDrawer-paper');
    if (drawer) {
      drawer.addEventListener('click', handleSidebarClick);
    } else {
      // If drawer isn't mounted yet, attach to document
      document.addEventListener('click', handleSidebarClick);
    }

    return () => {
      if (drawer) {
        drawer.removeEventListener('click', handleSidebarClick);
      }
      document.removeEventListener('click', handleSidebarClick);
    };
  }, [router.pathname]);

  const displayName = account?.displayName || account?.email;
  const displayEmail = account?.email;

  // Create session object for AppProvider
  const session = account
    ? {
        user: {
          name: displayName || null,
          email: displayEmail || null,
          image: account.photoURL,
        },
      }
    : null;

  // Create authentication object for AppProvider
  const authentication = {
    signIn: () => {
      router.navigate('/login');
      return Promise.resolve({});
    },
    signOut: async () => {
      if (isLoggingOut) {
        // If already logging out, prevent multiple calls
        return { success: false };
      }

      try {
        await logActivity(ACTIVITY_TYPES.LOGOUT, { reason: 'user_initiated' });
        await logout();
        return { success: true };
      } catch (_error) {
        showSnackbar('Error signing out. Please try again.', 'error');
        return { success: false };
      }
    },
  };

  const { mode } = useWorkspace(); // Extract mode here to pass to navigation

  return (
    <AppProvider
      navigation={
        getNavigationForUser(
          account,
          notifications,
          recentTenders,
          recentProcurements,
          mode
        ) as Navigation
      }
      router={router}
      theme={muiTheme}
      session={session}
      authentication={authentication}
      branding={{
        ...BRANDING,
        logo: (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <img
              src="/Bidlogiq_Logo-Long_Reversed.png"
              alt="BIDLOGIQ Logo"
              style={{
                height: 56,
                width: 'auto',
                maxHeight: 60,
                display: 'block',
              }}
            />
          </Box>
        ),
      }}
    >
      <ToolpadDashboardLayout
        sx={{
          backgroundColor: muiTheme.palette.bidlogiq.charcoal,
          '& main': {
            borderTopLeftRadius: { xs: 0, md: '16px' },
            backgroundColor: muiTheme.palette.background.default,
            // Remove overflow: hidden as it breaks scrolling
            display: 'flex',
            flexDirection: 'column',
          },
          '& .MuiDrawer-docked, & .MuiDrawer-paper': {
            width: `${muiTheme.drawerWidth}px !important`,
            minWidth: `${muiTheme.drawerWidth}px !important`,
            maxWidth: `${muiTheme.drawerWidth}px !important`,
            boxSizing: 'border-box',
            overflowX: 'hidden',
            backgroundColor: `${muiTheme.palette.bidlogiq.charcoal} !important`,
            color: `${muiTheme.palette.common.white} !important`,
          },
          '& .MuiAppBar-root': {
            backgroundColor: `${muiTheme.palette.bidlogiq.charcoal} !important`,
            color: `${muiTheme.palette.common.white} !important`,
            backgroundImage: 'none !important',
          },
          '& .MuiDrawer-paper .MuiListItemButton-root': {
            color: `${muiTheme.palette.common.white} !important`,
          },
          '& .MuiDrawer-paper .MuiListItemButton-root:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08) !important',
          },
          '& .MuiDrawer-paper .MuiListSubheader-root': {
            backgroundColor: 'transparent !important',
            color: `${muiTheme.palette.common.white} !important`,
            opacity: 0.7,
          },
          '& .MuiDrawer-paper .MuiDivider-root': {
            borderColor: 'rgba(255, 255, 255, 0.12) !important',
          },
          '& .MuiDrawer-paper .MuiListItemIcon-root, & .MuiDrawer-paper .MuiSvgIcon-root': {
            color: `${muiTheme.palette.common.white} !important`,
          },
          '& .MuiDrawer-paper .MuiTypography-root': {
            color: `${muiTheme.palette.common.white} !important`,
          },
          '& .MuiAppBar-root .MuiTypography-root, & .MuiAppBar-root .MuiIconButton-root, & .MuiAppBar-root .MuiSvgIcon-root':
            {
              color: `${muiTheme.palette.common.white} !important`,
            },
          '& .MuiToolbar-root': {
            padding: `${muiTheme.spacing(0, 3)} !important`,
          },
          // Disable the link behavior for the logo
          '& header a[href="/"], & header a[href=""], & header a[href="/dashboard"]': {
            pointerEvents: 'none !important',
            cursor: 'default !important',
          },
          '& .MuiContainer-root': {
            backgroundColor: muiTheme.palette.background.default,
            padding: muiTheme.spacing(1, 3),
          },
          '& [class*="PageContent-root"], & .MuiDrawer-docked + *': {
            marginLeft: '0 !important',
            paddingLeft: '0 !important',
          },
          '& [class*="Breadcrumbs-root"]': {
            display: 'none !important',
          },
          '& [data-testid="user-menu"]': isLoggingOut
            ? {
                pointerEvents: 'none',
                opacity: 0.7,
              }
            : {},
          '& a[href*="/tender/"]': {
            '&.Mui-selected': {
              backgroundColor: `${muiTheme.palette.bidlogiq.brick} !important`,
              color: `${muiTheme.palette.common.white} !important`,
            },
            '&.Mui-selected .MuiTypography-root': {
              color: `${muiTheme.palette.common.white} !important`,
              fontWeight: 'bold !important',
            },
            '&.Mui-selected .MuiListItemIcon-root, &.Mui-selected svg path': {
              color: `${muiTheme.palette.common.white} !important`,
              stroke: `${muiTheme.palette.common.white} !important`,
            },
          },
        }}
        slots={{
          toolbarActions: ToolbarActions,
        }}
        disableCollapsibleSidebar
      >
        {children}
      </ToolpadDashboardLayout>
    </AppProvider>
  );
}
